import React, { createContext, useEffect, useRef, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import auth from '../firebase/auth';
import db from '../firebase/firestore';
import functions from '../firebase/functions';
import { MEMBER_STATUS } from '../utils/constants';
import { normalizeMembership } from '../utils/membership';
import { hasInboxAccess } from '../utils/clubInbox';
import { clearPublicHome } from '../utils/publicHome';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState({});
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const profileUnsubRef = useRef(null);

  useEffect(() => {
    // Ohne Firebase-Konfiguration gibt es keinen Auth-Zustand zu beobachten.
    // Die öffentliche Seite läuft trotzdem - siehe firebase/config.js.
    if (!auth) {
      setLoading(false);
      return undefined;
    }

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      // Listener des vorherigen Nutzers abräumen, bevor ein neuer entsteht
      if (profileUnsubRef.current) {
        profileUnsubRef.current();
        profileUnsubRef.current = null;
      }

      setLoading(true);

      if (!firebaseUser) {
        setUser(null);
        setClaims({});
        setProfile(null);
        setLoading(false);
        return;
      }

      // forceRefresh, damit eine frisch gesetzte admin-Claim sofort sichtbar ist
      try {
        const tokenResult = await firebaseUser.getIdTokenResult(true);
        setClaims(tokenResult.claims || {});
      } catch {
        setClaims({});
      }
      setUser(firebaseUser);

      // Echtzeit-Listener: Eine Freigabe durch den Admin wirkt sofort,
      // ohne dass sich das Mitglied neu anmelden muss.
      let firstSnapshot = true;
      profileUnsubRef.current = onSnapshot(
        doc(db, 'users', firebaseUser.uid),
        (snap) => {
          setProfile(snap.exists() ? { uid: snap.id, ...snap.data() } : null);
          if (firstSnapshot) {
            firstSnapshot = false;
            setLoading(false);
          }
        },
        () => setLoading(false) // Fehler darf die App nicht blockieren
      );
    });

    return () => {
      if (typeof unsubscribeAuth === 'function') unsubscribeAuth();
      if (profileUnsubRef.current) profileUnsubRef.current();
    };
  }, []);

  // Eine klare Meldung schlägt einen kryptischen SDK-Fehler, wenn die
  // Konfiguration fehlt.
  function requireFirebase() {
    if (!auth) {
      throw new Error(
        'Die Anmeldung ist gerade nicht verfügbar. Bitte versuch es später erneut.'
      );
    }
  }

  function login(email, password) {
    requireFirebase();
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    // Das Kennzeichen „Startseite trotzdem zeigen" gilt nur für ein
    // angemeldetes Mitglied. Bliebe es stehen, hinge es wirkungslos in der
    // Sitzung herum und würde beim nächsten Anmelden im selben Tab die
    // Weiterleitung aushebeln.
    clearPublicHome();
    if (!auth) return Promise.resolve();
    return signOut(auth);
  }

  /**
   * „Passwort vergessen".
   *
   * Läuft seit v0.16.0 über unsere eigene Cloud Function statt über Firebase
   * Auths `sendPasswordResetEmail`. Der Link dahinter ist derselbe — er kommt
   * weiterhin von Firebase —, nur der Umschlag ist unserer: deutscher Text,
   * Absender `info@clubminithrills.de`, gleiches Aussehen wie die übrigen
   * Mails des Clubs.
   *
   * Die Function antwortet **immer** mit `ok`, auch bei unbekannter Adresse.
   * Das ist Absicht und trägt die Nicht-Auskunft, die vorher nur der
   * `catch`-Zweig in `Login.js` herstellte: Der Aufruf selbst verrät nicht
   * mehr, welche Adressen registriert sind.
   *
   * `requireFirebase()` bleibt: Ohne Konfiguration gibt es auch keine
   * Functions-Instanz, und ein Aufruf liefe in einen unverständlichen Fehler.
   */
  function resetPassword(email) {
    requireFirebase();
    return httpsCallable(functions, 'requestPasswordReset')({ email });
  }

  /**
   * Registrierung in zwei Schritten: Auth-Konto anlegen, dann das
   * Profil-Dokument über die Cloud Function. Der Client darf users/{uid}
   * nicht selbst schreiben - sonst könnte sich jemand direkt auf
   * status 'active' setzen.
   */
  async function register({ email, password, ...memberData }) {
    requireFirebase();
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    if (functions) {
      await httpsCallable(functions, 'registerMember')(memberData);
    }
    return credential.user;
  }

  async function refreshClaims() {
    if (!user) return;
    const tokenResult = await user.getIdTokenResult(true);
    setClaims(tokenResult.claims || {});
  }

  const isAdmin = claims?.admin === true;
  const memberStatus = profile?.status ?? null;
  // Admins sind immer drin, auch ohne Profil-Dokument (Bootstrap-Fall).
  const isActiveMember = isAdmin || memberStatus === MEMBER_STATUS.ACTIVE;
  // Organisator ist ein Profilfeld statt einer Claim - deshalb wirkt eine
  // Ernennung sofort: Der onSnapshot oben liest sie live mit, ohne dass sich
  // das Mitglied neu anmelden muesste. Admins sind immer auch Organisatoren.
  const isOrganizer = isAdmin || (isActiveMember && profile?.isOrganizer === true);
  // Zugehoerigkeit zum Club: Gast, Mitglied oder Crew. Unabhaengig von
  // isAdmin/isOrganizer - ein Admin kann Gast sein, ein Crew-Mitglied kein
  // Admin. Bestandsprofile ohne das Feld gelten als Mitglied, damit nach dem
  // Deploy niemand weniger sieht als vorher (siehe utils/membership.js).
  const membership = normalizeMembership(profile?.membership);
  // Club-Postfach: Admins immer, Organisatoren nur mit einzeln
  // freigeschaltetem Zugang. Nur für die Anzeige - die Grenze ziehen die
  // Rules und requireInboxAccess() auf dem Server.
  const canUseInbox = hasInboxAccess({ isAdmin, profile });

  const value = {
    user,
    claims,
    profile,
    loading,
    firebaseReady: !!auth,
    isAdmin,
    isOrganizer,
    canUseInbox,
    membership,
    memberStatus,
    isActiveMember,
    login,
    logout,
    register,
    resetPassword,
    refreshClaims,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
